import type { ReactNode } from "react";
import { cn } from "@shared/lib/utils";

type SectionHeaderProps = {
  /** Editorial section number — renders a mono `01 — LABEL` eyebrow. */
  index?: string;
  /** Eyebrow — tiny mono tracked-out label above the title. */
  label?: string;
  title: ReactNode;
  /** Muted one-liner under the title. */
  hint?: ReactNode;
  /** Right-aligned slot (badge, link, button). */
  action?: ReactNode;
  className?: string;
};

/**
 * Uniform section heading — numbered-editorial pattern: a mono eyebrow
 * ("01 — STATUS") above a sentence-case display title.
 */
export function SectionHeader({ index, label, title, hint, action, className }: SectionHeaderProps) {
  return (
    <header className={cn("flex items-end justify-between gap-3", className)}>
      <div className="min-w-0">
        {label || index ? (
          <p className="ty-eyebrow mb-1.5">
            {/* Number + em-dash carry the accent; the label stays ink-mute. */}
            {index ? (
              <span className="text-accent">
                {index}
                {label ? <span aria-hidden> — </span> : null}
              </span>
            ) : null}
            {label}
          </p>
        ) : null}
        <h2 className="truncate text-2xl font-bold text-ink">
          {title}
        </h2>
        {hint ? <p className="mt-1 text-xs leading-relaxed text-ink-mute">{hint}</p> : null}
      </div>
      {action ? <div className="shrink-0 pb-0.5">{action}</div> : null}
    </header>
  );
}
